import type {VendorPixel} from '../types/analytics/vendor-pixel'
import {clickUrl, isClickPixel, matchesTarget, pixelDedupKey} from './pixel-classifier'

/**
 * Fires a single vendor tracking pixel by creating a 1×1 image.
 * The request is fire-and-forget — no response handling needed.
 *
 * Used by page-view pixels. Click pixels use `firePixelsOnClick`, which fires
 * via a keepalive fetch so the request survives page navigation.
 */
export function fireVendorPixel(url: string): void {
  const img = new Image(1, 1)
  img.src = url
}

/**
 * Fires click pixels matching the given click payload.
 *
 * For each click pixel:
 *  - skip if the pixel's `targets` doesn't match `payload`
 *  - skip if the (pixel.key, payload) combo has already fired
 *  - otherwise fire the pixel's resolved URL via a `keepalive: true` fetch
 *    (so the request survives the user navigating away from the page) and
 *    record the dedup key
 *
 * `payload` is the parsed `data-analytics-event` JSON for the clicked CTA, or
 * `null` if no analytics payload is available (e.g. legacy
 * `data-vendor-pixel-event` click). New-shape pixels with `targets` won't
 * match a null payload — by design.
 */
export function firePixelsOnClick(
  pixels: VendorPixel[],
  payload: Record<string, unknown> | null,
  firedKeys?: Set<string>,
): void {
  for (const pixel of pixels) {
    if (!isClickPixel(pixel)) continue
    if (!matchesTarget(pixel.targets, payload)) continue

    const dedup = pixelDedupKey(pixel.key, pixel.type === 'cta-click' ? payload : null)
    if (firedKeys?.has(dedup)) continue

    const url = clickUrl(pixel)
    if (!url) continue

    fetchVendorPixel(url)
    firedKeys?.add(dedup)
  }
}

async function fetchVendorPixel(url: string): Promise<void> {
  // Using try/catch for style preferences defined by this rule: github/no-then
  try {
    await fetch(url, {
      method: 'GET',
      keepalive: true,
      mode: 'no-cors',
      credentials: 'include',
      cache: 'no-store',
    })
  } catch {
    // Fall back to image-based pixel. This is needed while we update the backend to add connect-src CSP
    // for enabled pixels. Once the backend is updated, we can remove this fallback.
    fireVendorPixel(url)
  }
}
